import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Upload, Copy, Check, Trash2 } from 'lucide-react';
import { apiPost } from '../../../lib/api.js';
import { SectionHeading, inputCls } from './ui.jsx';

const MAX_LOGO_BYTES = 5 * 1024 * 1024;

export default function PageDetailsSection({
  origin, publicUrl, bookingSlug, setBookingSlug,
  logoUrl, setLogoUrl, title, setTitle,
  instructions, setInstructions, redirectUrl, setRedirectUrl,
}) {
  const fileInputRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Booking link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy the link');
    }
  };

  const uploadFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, or WEBP)');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('File size exceeds 5MB limit');
      return;
    }
    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setLogoUrl(dataUrl);
      try {
        await apiPost('/booking/upload-logo', { logoUrl: dataUrl });
        toast.success('Booking page image uploaded!');
      } catch {
        toast.error('Failed to upload image');
      } finally {
        setIsUploading(false);
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read image');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="lg:col-span-6 space-y-5">
      <SectionHeading title="Page details" />

      {/* Booking page link */}
      <div className="space-y-1">
        <label className="block text-xs font-semibold text-white/70">Booking page link</label>
        <div className="flex items-center rounded border border-white/15 bg-[#140b22] overflow-hidden focus-within:border-civix-cyan focus-within:ring-1 focus-within:ring-civix-cyan/40 transition">
          <span className="px-3 py-2 text-xs text-white/50 bg-white/5 border-r border-white/15 select-none shrink-0 font-mono">
            {origin}/book/
          </span>
          <input
            type="text"
            maxLength={20}
            data-testid="booking-page-code"
            value={bookingSlug}
            onChange={(e) => setBookingSlug(e.target.value)}
            placeholder="yourBookingCode"
            className="flex-1 px-3 py-2 text-sm text-white bg-transparent outline-none font-medium placeholder:text-white/30"
          />
          <button
            type="button"
            data-testid="copy-booking-link-button"
            onClick={handleCopyLink}
            title={copied ? 'Copied!' : 'Copy link'}
            className="p-2.5 text-civix-purpleSoft hover:bg-civix-purple/10 transition cursor-pointer border-l border-white/10"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
        <div className="text-[11px] text-white/50 pt-0.5">
          For more help with booking codes, visit this{' '}
          <a href="#help" className="text-civix-cyan hover:underline">article.</a>
        </div>
      </div>

      {/* Logo uploader */}
      <div className="space-y-1">
        <label className="block text-xs font-semibold text-white/70">
          Booking page image / logo (optional)
        </label>
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            uploadFile(e.dataTransfer.files?.[0]);
          }}
          className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition flex flex-col items-center justify-center gap-2 group ${
            dragOver
              ? 'border-civix-cyan bg-civix-cyan/5'
              : 'border-white/15 hover:border-civix-cyan/60 bg-white/[0.02] hover:bg-civix-purple/5'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.gif,.png,.webp"
            className="hidden"
            onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ''; }}
          />
          {logoUrl ? (
            <div className="flex items-center gap-3">
              <img
                src={logoUrl}
                alt="Logo preview"
                className="w-14 h-14 object-contain rounded border border-white/15 bg-white p-1"
              />
              <div className="text-left">
                <div className="text-xs text-emerald-400 font-bold">Image attached</div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLogoUrl(''); }}
                  className="text-[11px] text-rose-400 hover:underline flex items-center gap-1 mt-1"
                >
                  <Trash2 className="w-3 h-3" /> Remove logo
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                data-testid="upload-image-button"
                disabled={isUploading}
                className="px-4 py-1.5 rounded border border-civix-purple text-civix-purpleSoft text-xs font-bold uppercase tracking-wide group-hover:bg-civix-purple group-hover:text-white transition flex items-center gap-2"
              >
                <Upload className="w-3.5 h-3.5" />
                {isUploading ? 'Uploading…' : 'UPLOAD IMAGE (MAX 5MB)'}
              </button>
              <div className="text-[11px] text-white/40">or drag and drop file</div>
            </>
          )}
        </div>
      </div>

      {/* Title */}
      <div className="space-y-1">
        <label className="block text-xs font-semibold text-white/70">
          Booking page title <span className="text-rose-400">*</span>
        </label>
        <input
          type="text"
          maxLength={80}
          required
          data-testid="booking-page-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Please select a time slot that would best fit your schedule."
          className={inputCls}
        />
      </div>

      {/* Instructions */}
      <div className="space-y-1">
        <label className="block text-xs font-semibold text-white/70">
          Booking instructions <span className="text-rose-400">*</span>
        </label>
        <textarea
          rows={3}
          maxLength={400}
          required
          data-testid="booking-page-subtitle"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Appts typically last between 5-10 minutes"
          className={`${inputCls} resize-y`}
        />
      </div>

      {/* Redirect */}
      <div className="space-y-1">
        <label className="block text-xs font-semibold text-white/70">
          Redirect to this URL after appointment is saved
        </label>
        <div className="flex items-center rounded border border-white/15 bg-[#140b22] overflow-hidden focus-within:border-civix-cyan focus-within:ring-1 focus-within:ring-civix-cyan/40 transition">
          <span className="px-3 py-2 text-xs text-white/50 bg-white/5 border-r border-white/15 select-none shrink-0 font-mono">
            https://
          </span>
          <input
            type="text"
            maxLength={120}
            data-testid="booking-page-redirectUrl"
            value={redirectUrl}
            onChange={(e) => setRedirectUrl(e.target.value)}
            placeholder="example.com/thank-you"
            className="flex-1 px-3 py-2 text-sm text-white bg-transparent outline-none placeholder:text-white/30"
          />
        </div>
        <div className="text-[11px] text-white/40">Leave blank for no redirect.</div>
      </div>
    </div>
  );
}
